/**
 * Comment & attachment contracts — DESIGN.md §6.8, §6.9 (R8, R17).
 */
import { z } from 'zod';
import { richTextSchema } from '../richtext/schema.js';
import { paginationSchema, uuidSchema } from './common.js';
import { userRefSchema } from './users.js';
export const attachmentSchema = z.object({
    id: uuidSchema,
    taskId: uuidSchema,
    commentId: uuidSchema.nullable(),
    filename: z.string(),
    mimeType: z.string(),
    sizeBytes: z.number().int().nonnegative(),
    width: z.number().int().nullable(),
    height: z.number().int().nullable(),
    /** Set once the worker's thumbnail job has run — §6.8. */
    hasThumbnail: z.boolean(),
    /** Canonical stream URL; the editor stores this in image nodes. */
    url: z.string(),
    thumbnailUrl: z.string().nullable(),
    uploadedBy: userRefSchema,
    createdAt: z.string(),
});
/**
 * One emoji on a comment, and who put it there — #42.
 *
 * Aggregated server-side rather than shipping a row per person: a comment
 * with forty 👍 is one entry with a count, and the names ride along for the
 * tooltip so nobody has to ask who agreed.
 */
export const commentReactionSchema = z.object({
    emoji: z.string(),
    count: z.number().int().positive(),
    /** Whether the viewer is one of them — the pill is a toggle. */
    mine: z.boolean(),
    /** Who reacted, for the tooltip. Capped server-side. */
    users: z.array(userRefSchema),
});
export const commentSchema = z.object({
    id: uuidSchema,
    taskId: uuidSchema,
    author: userRefSchema,
    /**
     * The agent that wrote it, or null when a person did — card #37.
     *
     * `author` stays the human who authorised the write either way (§12.5); an
     * empty string is an agent that sent no client name, which is why this is
     * nullable rather than optional. The client renders "Claude · for Brad".
     */
    authoredVia: z.string().nullable(),
    /** Null when soft-deleted — the feed renders "(comment removed)" (§6.9). */
    body: richTextSchema.nullable(),
    bodyText: z.string(),
    attachments: z.array(attachmentSchema),
    /** #42 — most-used first, then oldest first so the order is stable. */
    reactions: z.array(commentReactionSchema),
    createdAt: z.string(),
    editedAt: z.string().nullable(),
    deletedAt: z.string().nullable(),
});
export const createCommentRequestSchema = z
    .object({
    body: richTextSchema,
    /** Files uploaded before the comment was posted, adopted on save (§6.8). */
    attachmentIds: z.array(uuidSchema).max(20).default([]),
})
    .strict();
export const updateCommentRequestSchema = z.object({ body: richTextSchema }).strict();
/**
 * Toggle one emoji on a comment — #42.
 *
 * The set is deliberately closed. An open picker is a bigger component than
 * this feature is worth, and a shortlist is what people use anyway: an
 * acknowledgement, an agreement, a thanks, a laugh, and two for "I am on it"
 * and "this is a problem".
 */
export const COMMENT_REACTIONS = ['👍', '👎', '🎉', '😄', '👀', '❤️', '🚀', '😕'];
export const toggleReactionRequestSchema = z.object({ emoji: z.enum(COMMENT_REACTIONS) }).strict();
export const listCommentsQuerySchema = paginationSchema;
//# sourceMappingURL=discussion.js.map